import { makeAutoObservable } from 'mobx'

const MESSAGE_FLOW_MIN_LENGTH = 40
const MESSAGE_FLOW_MAX_LENGTH = 1000
const OPT_OUT_MIN_LENGTH = 20
const OPT_OUT_MAX_LENGTH = 1000
const IMAGE_MAX_SIZE_BYTES = 10 * 1024 * 1024
const IMAGE_ALLOWED_TYPES = ['image/jpeg', 'image/png']
const IMAGE_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']
const IMAGE_TYPE_ERROR = {
  title: 'The file is not supported for upload',
  desc: 'Please upload only: jpg, png',
}
const IMAGE_SIZE_ERROR = {
  desc: 'The image file is too large. It must not exceed 10 MB',
}

type IImageError = { title?: string; desc: string }

export class OptInMessageInfoStore {
  private _messageFlow = ''
  private _optInImage: File | null = null
  private _optInImageError: IImageError | null = null
  private _optOut = ''

  constructor() {
    makeAutoObservable(this)
  }

  get messageFlow() {
    return this._messageFlow
  }

  get optInImage() {
    return this._optInImage
  }

  get optInImageError() {
    return this._optInImageError
  }

  get optOut() {
    return this._optOut
  }

  get messageFlowMinLength() {
    return MESSAGE_FLOW_MIN_LENGTH
  }

  get messageFlowMaxLength() {
    return MESSAGE_FLOW_MAX_LENGTH
  }

  get optOutMinLength() {
    return OPT_OUT_MIN_LENGTH
  }

  get optOutMaxLength() {
    return OPT_OUT_MAX_LENGTH
  }

  private get _isMessageFlowValid() {
    const { length } = this._messageFlow
    return length >= MESSAGE_FLOW_MIN_LENGTH && length <= MESSAGE_FLOW_MAX_LENGTH
  }

  private get _isOptOutValid() {
    const { length } = this._optOut
    return length >= OPT_OUT_MIN_LENGTH && length <= OPT_OUT_MAX_LENGTH
  }

  get isNextDisabled() {
    return !(this._isMessageFlowValid && this._optInImage && this._isOptOutValid)
  }

  setMessageFlow = (value: string) => {
    this._messageFlow = value
  }

  setOptOut = (value: string) => {
    this._optOut = value
  }

  setOptInImage = (files: File[] | FileList) => {
    const [file] = Array.from(files)
    if (!file) return

    const error = this._getImageError(file)
    if (error) {
      this._optInImageError = error
      return
    }

    this._optInImageError = null
    this._optInImage = file
  }

  removeOptInImage = () => {
    this._optInImage = null
    this._optInImageError = null
  }

  clearOptInImageError = () => {
    this._optInImageError = null
  }

  private _getImageError = (file: File): IImageError | null => {
    if (!this._isImageTypeAllowed(file)) return IMAGE_TYPE_ERROR
    if (file.size > IMAGE_MAX_SIZE_BYTES) return IMAGE_SIZE_ERROR
    return null
  }

  private _isImageTypeAllowed = (file: File) => {
    if (IMAGE_ALLOWED_TYPES.includes(file.type)) return true

    const extension = file.name.split('.').pop()?.toLowerCase()
    return extension ? IMAGE_ALLOWED_EXTENSIONS.includes(extension) : false
  }

  reset = () => {
    this._messageFlow = ''
    this._optInImage = null
    this._optInImageError = null
    this._optOut = ''
  }
}
